import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { timer } from 'rxjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { filter } from 'rxjs/operators';
import { LocalStorageKeys, ProgressBar } from '../../../interfaces';
import { IProcessStatus } from '../../../interfaces/process.interface';
import { IOnboardingAccount, OnboardingSteps } from '../interfaces/onboarding.interface';
import { AccountService } from '@core/services/account/account.service';
import { UserService } from '@app/services/user/user.service';

@Injectable({
	providedIn: 'root',
})
export class OnboardingService {
	onboardingInProgress$ = new BehaviorSubject(false);
	onboardingSuccess$ = new BehaviorSubject<boolean>(false);
	progressBar: ProgressBar = {
		value: 0,
		accountsTotal: 0,
		accountsReady: 0,
	};
	process: IProcessStatus = IProcessStatus.loading;

	showMinifiedProgress$ = new BehaviorSubject(false);

	ignoreQueue = false;

	get onboardingQueue(): IOnboardingAccount[] {
		return JSON.parse(localStorage.getItem(LocalStorageKeys.ONBOARDING_QUEUE)) || [];
	}

	set onboardingQueue(array) {
		localStorage.setItem(LocalStorageKeys.ONBOARDING_QUEUE, JSON.stringify(array));
	}

	accountsLoading$ = new BehaviorSubject(false);
	onboardingQueue$ = new BehaviorSubject<IOnboardingAccount[]>(this.onboardingQueue);

	public readonly currentStep$: BehaviorSubject<OnboardingSteps> = new BehaviorSubject<OnboardingSteps>(
		OnboardingSteps.selectPermissions,
	);
	isUserProfileActive$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	constructor(private userService: UserService, private router: Router, private accountService: AccountService) {
		this.onboardingQueue$.subscribe((value) => (this.onboardingQueue = value));
		this.filterOnboardingAccounts();
		this.userService.user$
			.pipe(filter(Boolean))
			.subscribe(() => this.isUserProfileActive$.next(this.userService.userProfile === 'active'));
	}

	private get currentStep(): OnboardingSteps {
		return this.currentStep$.value;
	}

	public setStep(step: OnboardingSteps, route: string, provider: 'aws' | 'azure' | '' = 'aws'): void {
		if (this.currentStep === step) {
			return;
		}
		this.router.navigate([`onboarding/${provider}/${route}`]).then((_) => {
			this.currentStep$.next(step);
		});
	}

	/**
	 * Check accounts queue from localStorage for already onboarded accounts
	 */
	filterOnboardingAccounts() {
		this.accountsLoading$.next(true);
		this.accountService.getAccounts().subscribe({
			next: (accounts) => {
				const accountsInLs = this.onboardingQueue;
				const filteredAccounts = accountsInLs
					.filter(
						(accToOnboard) =>
							!accounts.find((onboardedAcc) => onboardedAcc.uuid === accToOnboard.account_uuid) ||
							accToOnboard.reonboarding,
					)
					.filter((accToOnboard) => accToOnboard.onboarding_status === 'unattempted');
				this.accountsLoading$.next(false);
				this.onboardingQueue$.next(filteredAccounts);
			},
			error: () => {
				this.accountsLoading$.next(false);
			},
		});
	}

	clearProgress() {
		this.progressBar.value = 0;
		this.progressBar.accountsReady = 0;
		this.progressBar.accountsTotal = 0;
	}

	calculateProgress(task) {
		const accountsTotal = task.accounts_total_count;
		const accountsLeft = task.accounts_count;
		if (accountsLeft === null || accountsLeft === undefined) {
			this.progressBar.accountsReady = 0;
		} else if (accountsLeft === 0) {
			this.progressBar.value = 100;
			this.progressBar.accountsReady = accountsTotal;
		} else {
			this.progressBar.accountsTotal = accountsTotal;
			this.progressBar.accountsReady = accountsTotal - accountsLeft;
			this.progressBar.value = ((accountsTotal - accountsLeft) / accountsTotal) * 100;
		}
	}

	onboardingSuccess() {
		if (this.showMinifiedProgress$.value) {
			// if modal is hidden
			this.onboardingInProgress$.next(false);
			this.onboardingSuccess$.next(true);
			this.onboardingSuccess$.next(false);
		} else {
			timer(1000).subscribe((_) => {
				this.onboardingSuccess$.next(false);
				this.onboardingInProgress$.next(false);
				this.clearProgress();
			});
		}
	}
}
